import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link as RouterLink } from 'react-router-dom';
import { getReportDefinitions } from '../../../api/reportsApi';
import { fetchReportValue } from '../../../api/reportValuesApi';
import { splitBySection } from '../reportSections';
import {
  DEFAULT_SHORTCUT_COUNT,
  FEATURED_REPORTS,
  WIDGET_VALUES,
  WIDGET_VALUE_SOURCES,
} from '../widgetsConfig';
import {
  DEFAULT_PERIOD_ID,
  PERIOD_OPTIONS,
  findPeriod,
} from '../periodOptions';
import { useI18n } from '../../../i18n';
import ExportMenu from './ExportMenu';
import { exportElementPdf } from '../export/exporters';

const reportTitle = (r, t) =>
  r?.reportTitle || r?.code || t('report.fallbackName', { id: r?.id });

const findReport = (reports, key) =>
  reports.find(
    (r) =>
      (r.code && r.code === key) ||
      (r.id !== undefined && r.id?.toString() === key?.toString()),
  );

const pickFeatured = (reports, configKeys, fallbackSlice) => {
  if (configKeys && configKeys.length > 0) {
    return configKeys.map((k) => findReport(reports, k)).filter(Boolean);
  }
  return reports.slice(...fallbackSlice);
};

const buildShortcutTo = (report, range) => {
  const params = new URLSearchParams();
  if (range?.from) params.set('from', range.from);
  if (range?.to) params.set('to', range.to);
  const qs = params.toString();
  return qs ? `/reports/${report.id}?${qs}` : `/reports/${report.id}`;
};

const formatWidgetValue = (raw, formatNumber) => {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return formatNumber(raw);
  }
  return String(raw);
};

const ReportValueWidget = ({ report, range, value, loading }) => {
  const { t, formatNumber } = useI18n();
  const hasValue = value && value.value !== undefined && value.value !== null;
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardActionArea
        component={RouterLink}
        to={buildShortcutTo(report, range)}
        sx={{ height: '100%', display: 'block' }}
      >
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
              mb: 1,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1.3 }}
              title={reportTitle(report, t)}
            >
              {reportTitle(report, t)}
            </Typography>
            <OpenInNewIcon
              sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 0.75,
              minHeight: 44,
            }}
          >
            {hasValue ? (
              <>
                <Typography
                  variant="h4"
                  component="div"
                  sx={{ fontWeight: 600, lineHeight: 1.1 }}
                >
                  {formatWidgetValue(value.value, formatNumber)}
                </Typography>
                {value.unit && (
                  <Typography variant="body2" color="text.secondary">
                    {value.unit}
                  </Typography>
                )}
              </>
            ) : loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} thickness={5} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: 'italic' }}
                >
                  {t('dashboard.loadingValue')}
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="body2"
                component="div"
                sx={{
                  color: 'text.disabled',
                  fontStyle: 'italic',
                  alignSelf: 'center',
                }}
              >
                {t('dashboard.noDataYet')}
              </Typography>
            )}
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.5, minHeight: 18 }}
          >
            {value?.caption || (report.code ? report.code : ' ')}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const PeriodSelect = ({ value, onChange, disabled }) => {
  const { t } = useI18n();
  return (
    <FormControl size="small" sx={{ minWidth: 200 }} disabled={disabled}>
      <InputLabel id="dashboard-period-label">{t('period.label')}</InputLabel>
      <Select
        labelId="dashboard-period-label"
        label={t('period.label')}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {PERIOD_OPTIONS.map((opt) => (
          <MenuItem key={opt.id} value={opt.id}>
            {t(opt.labelKey)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const DashboardWidgets = () => {
  const { t, formatDate, formatDateTime } = useI18n();
  const containerRef = useRef(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [periodId, setPeriodId] = useState(DEFAULT_PERIOD_ID);
  // Per-report-id state for the SEAL-derived dynamic values:
  //   { [reportId]: { loading: bool, value: {value, unit, caption} | null } }
  const [dynamicValues, setDynamicValues] = useState({});
  // Generation token so a late SEAL response from a previous period does not
  // overwrite the current period's value.
  const currentGenRef = useRef(null);

  const range = useMemo(() => findPeriod(periodId).range(), [periodId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getReportDefinitions({
      pageNumber: 1,
      pageSize: 100,
      sortField: 'reportTitle',
      sortOrder: 'Ascending',
    })
      .then((data) => {
        if (cancelled) return;
        setReports(splitBySection(data).reports);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(t('dashboard.loadReportsFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const shortcutReports = useMemo(
    () =>
      pickFeatured(reports, FEATURED_REPORTS.shortcuts, [
        0,
        DEFAULT_SHORTCUT_COUNT,
      ]),
    [reports],
  );
  // Fetch SEAL-derived widget values when the report list or period changes.
  // Each fetch is independent (parallel); failures are swallowed and the
  // widget falls back to its empty state. Stale responses (period changed
  // mid-fetch) are dropped via a generation token.
  useEffect(() => {
    if (shortcutReports.length === 0) return undefined;
    const generation = Symbol('widget-values-gen');
    // eslint-disable-next-line no-use-before-define
    currentGenRef.current = generation;

    const targets = shortcutReports
      .map((r) => ({ report: r, source: r.code && WIDGET_VALUE_SOURCES[r.code] }))
      .filter((t) => t.source && t.report.path);

    if (targets.length === 0) return undefined;

    setDynamicValues((prev) => {
      const next = { ...prev };
      targets.forEach((t) => {
        next[t.report.id] = { loading: true, value: null };
      });
      return next;
    });

    // All tiles are requested at once: the /_seal/value proxy reuses one SEAL
    // session, caps concurrent SEAL executions (SEAL_VALUE_CONCURRENCY) and
    // caches values per period, so SEAL's own queue is never flooded.
    targets.forEach(({ report: r, source }) => {
      (async () => {
        const tag = r.code || `id=${r.id}`;
        const t0 = Date.now();
        // eslint-disable-next-line no-console
        console.log(
          `[widget-value] ${tag} START column=${source.column} aggregate=${source.aggregate} from=${range.from} to=${range.to}`,
        );
        try {
          const res = await fetchReportValue({
            reportId: r.id,
            path: r.path,
            column: source.column,
            aggregate: source.aggregate,
            modelName: source.modelName,
            from: range.from,
            to: range.to,
          });
          const dt = Date.now() - t0;
          if (currentGenRef.current !== generation) {
            // eslint-disable-next-line no-console
            console.log(`[widget-value] ${tag} STALE (${dt}ms) — discarded`);
            return;
          }
          const v =
            res && res.value !== null && res.value !== undefined
              ? {
                  value: res.value,
                  unit: source.unit,
                  caption: source.caption,
                }
              : null;
          if (v) {
            // eslint-disable-next-line no-console
            console.log(`[widget-value] ${tag} OK value=${res.value} (${dt}ms)`);
          } else {
            // eslint-disable-next-line no-console
            console.warn(
              `[widget-value] ${tag} EMPTY (${dt}ms) reason=${res?.reason} — configured model="${source.modelName}" column="${source.column}". Tables SEAL returned:`,
              res?.debug?.tablesFound,
              res?.debug,
            );
          }
          setDynamicValues((prev) => ({
            ...prev,
            [r.id]: { loading: false, value: v },
          }));
        } catch (err) {
          const dt = Date.now() - t0;
          if (currentGenRef.current !== generation) return;
          // eslint-disable-next-line no-console
          console.error(`[widget-value] ${tag} ERROR (${dt}ms)`, err.message);
          setDynamicValues((prev) => ({
            ...prev,
            [r.id]: { loading: false, value: null },
          }));
        }
      })();
    });

    return () => {
      // Mark this generation stale so any in-flight responses are ignored.
      if (currentGenRef.current === generation) {
        currentGenRef.current = null;
      }
    };
  }, [shortcutReports, range.from, range.to]);

  const valuesLoading = Object.values(dynamicValues).some((v) => v.loading);

  const exportFormats = [
    {
      id: 'pdf',
      onExport: () =>
        exportElementPdf(containerRef.current, `indicatori_${range.from}_${range.to}`, {
          headerLines: [
            t('dashboard.indicators'),
            t('export.periodRange', {
              period: t(findPeriod(periodId).labelKey),
              from: formatDate(`${range.from}T00:00:00`),
              to: formatDate(`${range.to}T00:00:00`),
            }),
            t('export.generatedAt', { date: formatDateTime(new Date()) }),
          ],
          ignoreSelector: '[data-export-ignore]',
        }),
    },
  ];

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 3 }}>
        {error}
      </Alert>
    );
  }

  if (!loading && shortcutReports.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 3 }} ref={containerRef}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Typography variant="h6">{t('dashboard.indicators')}</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <PeriodSelect
            value={periodId}
            onChange={setPeriodId}
            disabled={loading}
          />
          <ExportMenu
            formats={exportFormats}
            disabled={loading || valuesLoading}
          />
        </Stack>
      </Stack>
      <Grid container spacing={2}>
        {loading
          ? Array.from({ length: DEFAULT_SHORTCUT_COUNT }).map((_, i) => (
              <Grid item xs={12} sm={6} md={3} key={`s-skel-${i}`}>
                <Skeleton variant="rounded" height={96} />
              </Grid>
            ))
          : shortcutReports.map((r) => {
              // Resolution order: hard-coded override → dynamic SEAL value.
              const staticValue = r.code ? WIDGET_VALUES[r.code] : undefined;
              const dyn = dynamicValues[r.id];
              const value = staticValue || (dyn && dyn.value) || undefined;
              const widgetLoading = !!(dyn && dyn.loading);
              return (
                <Grid item xs={12} sm={6} md={3} key={`s-${r.id}`}>
                  <ReportValueWidget
                    report={r}
                    range={range}
                    value={value}
                    loading={widgetLoading}
                  />
                </Grid>
              );
            })}
      </Grid>
    </Box>
  );
};

export default DashboardWidgets;
